import {readBahaiYear} from '@liquid-spirit/bahai-calendar/years';
import {isCommunityYearContext, yearSelector} from './community-years.js';
// Shared budget endpoint contracts.
//
// Treasury owns treasury fund/payment-intent endpoints. This domain owns the
// full `/api/budgets/*` and `/api/budget-allocations/*` lifecycle used by the
// main web app.

export const BUDGET_PATHS = {
  collection: '/api/budgets',
  current: '/api/budgets/current',
  allocations: '/api/budget-allocations',
};

const enc = (value) => encodeURIComponent(value);

export const buildBudgetPath = (budgetId) => `${BUDGET_PATHS.collection}/${enc(budgetId)}`;
export const buildBudgetApprovePath = (budgetId) => `${buildBudgetPath(budgetId)}/approve`;
export const buildBudgetAllocationsPath = (budgetId) => `${buildBudgetPath(budgetId)}/allocations`;
export const buildBudgetAllocationRequestsPath = (budgetId) =>
  `${buildBudgetPath(budgetId)}/allocation-requests`;
export const buildBudgetAllocationPath = (allocationId) =>
  `${BUDGET_PATHS.allocations}/${enc(allocationId)}`;
export const buildBudgetAllocationApprovePath = (allocationId) =>
  `${buildBudgetAllocationPath(allocationId)}/approve`;
export const buildBudgetAllocationDeclinePath = (allocationId) =>
  `${buildBudgetAllocationPath(allocationId)}/decline`;

export class BudgetResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} response: ${detail}`);
    this.name = 'BudgetResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

export class BudgetRequestContractError extends Error {
  constructor(detail) {
    super(`Invalid budget request: ${detail}`);
    this.name = 'BudgetRequestContractError';
    this.code = 'INVALID_API_REQUEST';
  }
}

export function readBudgetIdentifier(value) {
  if (typeof value !== 'string' || !/^[a-f0-9]{24}$/i.test(value)) throw new BudgetRequestContractError('expected a budget, allocation or fund ID.');
  return value;
}

// Numeric text is accepted by existing forms; objects, booleans and blank text
// must never be coerced into money. Optional null text/fund values clear fields.
const validBudgetYear = value => {try {return readBahaiYear(value) === value;} catch {return false;}};
const requestNumber = value => (typeof value === 'number' || typeof value === 'string' && value.trim() !== '') && Number.isFinite(Number(value));
const date = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:T.+)?$/.test(value) && Number.isFinite(Date.parse(value));
export function readBudgetWriteRequest(payload, operation) {
  const fail = detail => { throw new BudgetRequestContractError(detail); };
  if (!isRecord(payload)) fail('expected an object.');
  const budget = ['create', 'update', 'approve'].includes(operation);
  const fields = budget ? ['name', 'periodLabel', 'currency', 'notes', 'yearReference'] : ['title', 'categoryKey', 'description', 'notes', 'decisionNotes'];
  for (const field of fields) if (payload[field] !== undefined && !(typeof payload[field] === 'string' || payload[field] === null && !['name', 'title'].includes(field))) fail(`${field} must be text.`);
  if (budget) {
    if (payload.bahaiYear !== undefined && !validBudgetYear(payload.bahaiYear)) fail('choose a supported Bahá’í year.');
    for (const field of ['financialStartDate', 'financialEndDate']) if (payload[field] !== undefined && (!date(payload[field]) || payload[field].length !== 10 || new Date(payload[field]).toISOString().slice(0, 10) !== payload[field])) fail('enter valid financial period calendar dates.');
    for (const field of ['startDate', 'endDate']) if (payload[field] !== undefined && !date(payload[field])) fail(`${field} must be a date.`);
    if (payload.status !== undefined && !['draft', 'approved', 'archived'].includes(payload.status)) fail('invalid budget status.');
    if (operation === 'create' && payload.bahaiYear === undefined && (!payload.name?.trim() || !date(payload.startDate) || !date(payload.endDate))) fail('name, startDate and endDate are required.');
    if (payload.name !== undefined && !payload.name.trim()) fail('name is required.');
    if (payload.startDate && payload.endDate && Date.parse(payload.endDate) < Date.parse(payload.startDate)) fail('endDate precedes startDate.');
    if (operation === 'approve' && (typeof payload.notes !== 'string' || !payload.notes.trim())) fail('an institutional approval reference is required.');
  } else {
    for (const field of ['allocatedAmount', 'actualAmount', 'sortOrder']) if (payload[field] !== undefined && (!requestNumber(payload[field]) || field !== 'sortOrder' && Number(payload[field]) < 0)) fail(`${field} must be a finite ${field === 'sortOrder' ? 'number' : 'non-negative number'}.`);
    if (payload.fundId !== undefined && payload.fundId !== null && payload.fundId !== '') readBudgetIdentifier(payload.fundId);
    if (['createAllocation', 'requestAllocation'].includes(operation) && (!payload.title?.trim() || !requestNumber(payload.allocatedAmount))) fail('title and allocatedAmount are required.');
    if (operation === 'updateAllocation' && payload.title !== undefined && !payload.title.trim()) fail('title is required.');
    if (operation === 'requestAllocation' && Number(payload.allocatedAmount) <= 0 || operation === 'approveAllocationRequest' && payload.allocatedAmount !== undefined && Number(payload.allocatedAmount) <= 0) fail('amount must be greater than zero.');
  }
  for (const field of ['community', 'communityId']) if (payload[field] != null && payload[field] !== '') readBudgetIdentifier(payload[field]);
  return payload;
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
// Native JSON adapters return `{ data: responseBody }`; web adapters return
// the body directly. Validate either transport boundary while preserving the
// original return shape for existing callers.
const responseBody = payload => isRecord(payload) && payload.success === undefined && isRecord(payload.data)
  ? payload.data
  : payload;
const hasIdentifier = value => isRecord(value)
  && typeof (value.id || value._id) === 'string'
  && Boolean(value.id || value._id);
const BUDGET_TOTAL_KEYS = ['allocatedAmount', 'actualAmount', 'committedAmount', 'uncommittedAmount', 'remainingAmount'];
const text = value => typeof value === 'string';
const identifier = value => text(value) && Boolean(value);
const optional = (row, key, check) => row[key] === undefined || check(row[key]);
const nullable = check => value => value === null || check(value);
const safe = row => !['password', 'passwordHash', 'verificationCode', 'resetPasswordToken', 'refreshToken', 'accessToken'].some(key => Object.hasOwn(row, key));
const dates = row => ['createdAt', 'updatedAt', 'reviewedAt', 'approvedAt'].every(key => optional(row, key, nullable(date)));
const person = value => value === null || isRecord(value) && hasIdentifier(value) && text(value.fullName) && optional(value, 'email', text) && safe(value);
const money = row => BUDGET_TOTAL_KEYS.every(key => Number.isFinite(row[key])) && optional(row, 'advanceOutstandingAmount', Number.isFinite);
const allocation = row => isRecord(row) && hasIdentifier(row) && safe(row)
  && identifier(row.title) && identifier(row.categoryKey)
  && ['pending', 'approved', 'declined'].includes(row.status) && money(row)
  && ['budgetId', 'communityId'].every(key => optional(row, key, identifier))
  && optional(row, 'requestOrigin', nullable(value => ['budget_setup', 'funding_request'].includes(value)))
  && optional(row, 'fundId', nullable(identifier))
  && ['description', 'notes', 'decisionNotes'].every(key => optional(row, key, text))
  && ['fundName', 'fundCode', 'fundChain', 'fundTokenSymbol', 'fundDisplaySymbol'].every(key => optional(row, key, nullable(text)))
  && optional(row, 'fundAssetType', value => ['fiat', 'crypto'].includes(value))
  && optional(row, 'fundVisibility', value => ['internal', 'community', 'public'].includes(value))
  && ['isSummary', 'fundIsOnchainTracked'].every(key => optional(row, key, value => typeof value === 'boolean'))
  && optional(row, 'sortOrder', Number.isFinite)
  && optional(row, 'fundPolicy', value => isRecord(value) && ['donationOnly', 'spendable', 'requiresApprovalForOutflow'].every(key => typeof value[key] === 'boolean'))
  && ['createdBy', 'updatedBy', 'reviewedBy'].every(key => optional(row, key, person)) && dates(row);

const isBudgetSummary = budget => isRecord(budget)
  && hasIdentifier(budget)
  && typeof budget.name === 'string' && Boolean(budget.name)
  && ['draft', 'approved', 'archived'].includes(budget.status)
  && typeof budget.currency === 'string' && Boolean(budget.currency)
  && isRecord(budget.totals)
  && money(budget.totals) && safe(budget)
  && ['communityId'].every(key => optional(budget, key, identifier))
  && optional(budget, 'bahaiYear', nullable(validBudgetYear))
  && ['periodLabel', 'notes'].every(key => optional(budget, key, text))
  && ['startDate', 'endDate'].every(key => optional(budget, key, date))
  && optional(budget, 'approvedBy', nullable(identifier)) && dates(budget);

export function readBudgetListResponse(payload) {
  const body = responseBody(payload);
  if (!isRecord(body) || body.success !== true || !optional(body, 'currentBahaiYear', validBudgetYear) || !Array.isArray(body.data)
    || !body.data.every(isBudgetSummary)) {
    throw new BudgetResponseContractError('budget list', 'expected a data array of budget summaries.');
  }
  return payload;
}

export function readBudgetDetailResponse(payload) {
  const body = responseBody(payload);
  const detail = body?.data;
  if (!isRecord(body) || body.success !== true || !isRecord(detail)
    || !isBudgetSummary(detail.budget) || !Array.isArray(detail.allocations)
    || !Array.isArray(detail.requests) || ![...detail.allocations, ...detail.requests].every(row => allocation(row)
      && (row.budgetId === undefined || row.budgetId === (detail.budget.id || detail.budget._id))
      && (row.communityId === undefined || row.communityId === detail.budget.communityId))
    || !detail.allocations.every(row => row.status === 'approved')) {
    throw new BudgetResponseContractError('budget detail', 'expected a budget with allocations and requests arrays.');
  }
  return payload;
}

export function readCurrentBudgetResponse(payload) {
  const body = responseBody(payload);
  const detail = body?.data;
  if (detail?.budget !== null) {
    readBudgetDetailResponse(payload);
    if (detail.communityYearContext !== undefined && (!isCommunityYearContext(detail.communityYearContext) || detail.budget.communityYearId !== detail.communityYearContext.id || detail.budget.communityId !== detail.communityYearContext.communityId || detail.budget.bahaiYear !== detail.communityYearContext.year.number)) throw new BudgetResponseContractError('current budget', 'the budget must belong to its returned community year.');
    return payload;
  }
  if (body.success !== true || !isCommunityYearContext(detail.communityYearContext) || !Array.isArray(detail.allocations) || detail.allocations.length || !Array.isArray(detail.requests) || detail.requests.length) throw new BudgetResponseContractError('current budget', 'expected a year-scoped missing budget.');
  return payload;
}

export function readBudgetMutationResponse(payload) {
  readBudgetDetailResponse(payload);
  const body = responseBody(payload);
  if ((body.data.allocationId !== undefined && !identifier(body.data.allocationId))
    || (body.data.requestId !== undefined && !identifier(body.data.requestId))) {
    throw new BudgetResponseContractError('budget mutation', 'expected a detail envelope with optional allocation or request identifier.');
  }
  return payload;
}

export const createBudgetsClient = (adapter) => {
  if (!adapter || typeof adapter !== 'object') {
    throw new TypeError('createBudgetsClient requires a transport adapter.');
  }

  return {
    getCurrent(options = {}) {
      const {year, ...transport} = options;
      const selected = year === undefined ? 'current' : yearSelector(year);
      return adapter.get(`${BUDGET_PATHS.current}${selected === 'current' ? '' : `?year=${selected}`}`, transport).then(payload => {
        readCurrentBudgetResponse(payload);
        const context = responseBody(payload).data.communityYearContext;
        if (selected !== 'current' && (!context || context.year.number !== selected)) throw new BudgetResponseContractError('current budget', 'the response year differs from the requested year.');
        return payload;
      });
    },
    list(options = {}) {
      return adapter.get(BUDGET_PATHS.collection, options).then(readBudgetListResponse);
    },
    getById(budgetId, options = {}) {
      readBudgetIdentifier(budgetId);
      return adapter.get(buildBudgetPath(budgetId), options).then(readBudgetDetailResponse);
    },
    create(payload, options = {}) {
      readBudgetWriteRequest(payload, 'create');
      return adapter.post(BUDGET_PATHS.collection, payload, options).then(readBudgetMutationResponse);
    },
    update(budgetId, payload, options = {}) {
      readBudgetIdentifier(budgetId); readBudgetWriteRequest(payload, 'update');
      return adapter.put(buildBudgetPath(budgetId), payload, options).then(readBudgetMutationResponse);
    },
    approve(budgetId, payload = {}, options = {}) {
      readBudgetIdentifier(budgetId); readBudgetWriteRequest(payload, 'approve');
      return adapter.post(buildBudgetApprovePath(budgetId), payload, options).then(readBudgetMutationResponse);
    },
    createAllocation(budgetId, payload, options = {}) {
      readBudgetIdentifier(budgetId); readBudgetWriteRequest(payload, 'createAllocation');
      return adapter.post(buildBudgetAllocationsPath(budgetId), payload, options).then(readBudgetMutationResponse);
    },
    requestAllocation(budgetId, payload, options = {}) {
      readBudgetIdentifier(budgetId); readBudgetWriteRequest(payload, 'requestAllocation');
      return adapter.post(buildBudgetAllocationRequestsPath(budgetId), payload, options).then(readBudgetMutationResponse);
    },
    updateAllocation(allocationId, payload, options = {}) {
      readBudgetIdentifier(allocationId); readBudgetWriteRequest(payload, 'updateAllocation');
      return adapter.put(buildBudgetAllocationPath(allocationId), payload, options).then(readBudgetMutationResponse);
    },
    deleteAllocation(allocationId, options = {}) {
      readBudgetIdentifier(allocationId);
      return adapter.delete(buildBudgetAllocationPath(allocationId), options).then(readBudgetMutationResponse);
    },
    approveAllocationRequest(allocationId, payload = {}, options = {}) {
      readBudgetIdentifier(allocationId); readBudgetWriteRequest(payload, 'approveAllocationRequest');
      return adapter.post(buildBudgetAllocationApprovePath(allocationId), payload, options).then(readBudgetMutationResponse);
    },
    declineAllocationRequest(allocationId, payload = {}, options = {}) {
      readBudgetIdentifier(allocationId); readBudgetWriteRequest(payload, 'declineAllocationRequest');
      return adapter.post(buildBudgetAllocationDeclinePath(allocationId), payload, options).then(readBudgetMutationResponse);
    },
  };
};
